import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { BellPlus, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const UNASSIGNED = '__none__';

const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const quickDates = () => {
  const t = new Date();
  const tomorrow = new Date(t); tomorrow.setDate(t.getDate() + 1);
  const fri = new Date(t); fri.setDate(t.getDate() + ((5 - t.getDay() + 7) % 7 || 7));
  const nextWk = new Date(t); nextWk.setDate(t.getDate() + 7);
  return [['Tomorrow', isoDate(tomorrow)], ['Friday', isoDate(fri)], ['Next Week', isoDate(nextWk)]];
};

let metaCache = null;
const loadMeta = async () => {
  if (!metaCache) metaCache = (await axios.get(`${API}/admin/billing/followup/meta`, { withCredentials: true })).data;
  return metaCache;
};

// Add / edit / resolve a billing follow-up on an invoice (recordType="invoice") or job ("job").
export const BillingFollowUpDialog = ({ open, onOpenChange, recordType, recordId, followup, title, onSaved }) => {
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState({ reason: '', due_date: '', assigned_to_id: UNASSIGNED, note: '' });
  const [saving, setSaving] = useState(false);
  const isOpen = followup && !followup.resolved;

  useEffect(() => {
    if (!open) return;
    loadMeta().then(setMeta).catch(() => toast.error('Failed to load follow-up options'));
    setForm(isOpen
      ? { reason: followup.reason, due_date: followup.due_date, assigned_to_id: followup.assigned_to_id || UNASSIGNED, note: followup.note || '' }
      : { reason: '', due_date: '', assigned_to_id: UNASSIGNED, note: '' });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!form.reason || !form.due_date) { toast.error('Pick a reason and a follow-up date'); return; }
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/billing/followup/${recordType}/${recordId}`, {
        ...form, assigned_to_id: form.assigned_to_id === UNASSIGNED ? null : form.assigned_to_id,
      }, { withCredentials: true });
      toast.success('Follow-up saved');
      onSaved?.(data.billing_followup);
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save follow-up'); }
    setSaving(false);
  };

  const resolve = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/billing/followup/${recordType}/${recordId}/resolve`, { note: form.note }, { withCredentials: true });
      toast.success('Follow-up resolved');
      onSaved?.(data.billing_followup);
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to resolve'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="billing-followup-dialog" className="bg-[#141414] border-[#262626] text-white max-w-md z-[80]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-['Outfit']"><BellPlus className="w-5 h-5 text-[#D4AF37]" /> {isOpen ? 'Edit Follow-Up' : 'Add Reminder / Follow-Up'}</DialogTitle>
          {title && <p className="text-xs text-[#A3A3A3]">{title}</p>}
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Follow-Up Reason</Label>
            <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
              <SelectTrigger data-testid="followup-reason-select" className="bg-[#0A0A0A] border-[#262626] mt-1"><SelectValue placeholder="Why does this need revisited?" /></SelectTrigger>
              <SelectContent className="bg-[#141414] border-[#262626] z-[90]">
                {(meta?.reasons || []).map((r) => <SelectItem key={r.key} value={r.key} data-testid={`followup-reason-${r.key}`} className="text-white">{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Follow-Up Date</Label>
            <Input data-testid="followup-date-input" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="bg-[#0A0A0A] border-[#262626] mt-1" />
            <div className="flex gap-1.5 mt-1.5">
              {quickDates().map(([lbl, d]) => (
                <button key={lbl} type="button" data-testid={`followup-quick-${lbl.toLowerCase().replace(' ', '-')}`} onClick={() => setForm({ ...form, due_date: d })} className="text-[11px] px-2 py-0.5 rounded border border-[#3f3f3f] text-[#A3A3A3] hover:text-white">{lbl}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Assigned To</Label>
            <Select value={form.assigned_to_id} onValueChange={(v) => setForm({ ...form, assigned_to_id: v })}>
              <SelectTrigger data-testid="followup-assignee-select" className="bg-[#0A0A0A] border-[#262626] mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#141414] border-[#262626] z-[90]">
                <SelectItem value={UNASSIGNED} className="text-white">Unassigned</SelectItem>
                {(meta?.assignees || []).map((a) => <SelectItem key={a.id} value={a.id} className="text-white">{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Follow-Up Note</Label>
            <Textarea data-testid="followup-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} placeholder="e.g. Waiting on estimate from Adam" className="bg-[#0A0A0A] border-[#262626] mt-1" />
          </div>
          <div className="flex justify-between gap-2 pt-1">
            {isOpen ? (
              <Button data-testid="followup-resolve-btn" variant="outline" disabled={saving} onClick={resolve} className="border-green-600/50 text-green-300 hover:bg-green-500/10"><CheckCircle2 className="w-4 h-4 mr-1" /> Mark Resolved</Button>
            ) : <span />}
            <Button data-testid="followup-save-btn" disabled={saving} onClick={save} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold">{saving ? 'Saving…' : 'Save Follow-Up'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Compact summary chip of an existing follow-up.
export const FollowUpChip = ({ followup, today, testid, onClick }) => {
  if (!followup || followup.resolved) return null;
  const overdue = today && followup.due_date < today;
  const Tag = onClick ? 'button' : 'span';
  return (
    <Tag type={onClick ? 'button' : undefined} onClick={onClick} data-testid={testid} className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${onClick ? 'cursor-pointer hover:brightness-125' : ''} ${overdue ? 'text-red-300 border-red-500/50 bg-red-500/10' : 'text-emerald-300 border-emerald-600/50 bg-emerald-500/10'}`}>
      Follow-up: {followup.reason_label} · {followup.due_date}{followup.assigned_to_name ? ` · ${followup.assigned_to_name}` : ''}
    </Tag>
  );
};

export default BillingFollowUpDialog;
